import { PretestQuestionsQuery } from '~/data';
import { ItemDefinition } from '~/training/sequence/templates/items/Item';
import {
  ANSWER_BUTTON,
  AUDIO,
  BACKGROUND,
  CARD,
  EMAIL,
  HORIZONTAL_AUDIO_PLAYER,
  IMAGE,
  ItemIds,
  LAYOUT_COLUMNS,
  LAYOUT_HALF_PAGE,
  LAYOUT_LIST,
  QUESTION_SEQUENCE,
  QUIZ_CHOICE,
  ROOT,
  TEXT
} from '~/training/sequence/templates/items/itemIds';

export type PretestQuestion = NonNullable<
  PretestQuestionsQuery['learnerTraining']['pretest']
>['questions'][number];

type SlideItem = NonNullable<PretestQuestion['sequence']['items']>[number];
type QuestionItem = Extract<SlideItem, { __typename?: 'Question' }>;

const isQuestion = (item: SlideItem): item is QuestionItem =>
  item.__typename === 'Question';

/** The QUESTION item that was drawn: its id is the pretest question id. */
export const drawnQuestion = (question: PretestQuestion) =>
  question.sequence.items?.find(
    (item): item is QuestionItem => isQuestion(item) && item.id === question.id
  );

/**
 * The question's slide with only the drawn question on it, so a slide with
 * several questions (a card carousel, say) asks just the one drawn.
 */
export const slideForQuestion = (
  question: PretestQuestion
): PretestQuestion['sequence'] => ({
  ...question.sequence,
  items: question.sequence.items?.filter(
    item => !isQuestion(item) || item.id === question.id
  )
});

/** The options learners choose from, and whether more than one is right. */
export const questionOptions = (question: PretestQuestion) => {
  const options = drawnQuestion(question)?.items ?? [];

  return {
    optionIds: options.map(option => option.id),
    multiple: question.multiple
  };
};

/**
 * Items a regular template may use to ask a pretest question: layout, media,
 * the email a phishing question is about (it only displays), the quiz frame
 * and its answer buttons, which have a pretest mode (see PretestAnswers).
 * Anything else might save, reveal, branch or navigate, so a slide that uses
 * it is asked with the plain pretest question view instead.
 */
const PRETEST_TEMPLATE_ITEMS = new Set<ItemIds>([
  ROOT,
  BACKGROUND,
  LAYOUT_LIST,
  LAYOUT_COLUMNS,
  LAYOUT_HALF_PAGE,
  CARD,
  EMAIL,
  TEXT,
  IMAGE,
  AUDIO,
  HORIZONTAL_AUDIO_PLAYER,
  QUIZ_CHOICE,
  QUESTION_SEQUENCE,
  ANSWER_BUTTON
]);

/**
 * True when the translated slide asks the question only through answer
 * buttons, one for each option.
 */
export const canAskWithTemplate = (
  definition: ItemDefinition<ItemIds>,
  optionIds: string[]
) => {
  const answerButtons = new Set<string>();
  let supported = true;

  const visit = (item: ItemDefinition<ItemIds>) => {
    if (!PRETEST_TEMPLATE_ITEMS.has(item.type)) supported = false;
    if (item.type === ANSWER_BUTTON) answerButtons.add(item.id);
    item.items?.forEach(visit);
  };
  visit(definition);

  return (
    supported &&
    optionIds.length > 0 &&
    optionIds.every(id => answerButtons.has(id))
  );
};
